'use client';

import { useRef, useState, type KeyboardEvent } from 'react';
import { Check } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { BubbleActions, BubbleRows } from '@/components/chat/BubbleBlock';
import type { SlotPickerBlock } from '@/lib/chat/blocks/slot-picker';

/**
 * Up to three free times, one marked as the best fit or the soonest, and one
 * orange tap to pencil the chosen one in (`slot-picker.ts`).
 *
 * 🚨 The orange tap does not write. It sends the slot's `instruction` — words
 * the server built — as the owner's own message, and Mucka books it through
 * the ordinary `create_job` path, pencilled, with every check that path has.
 * Pencilled texts nobody; confirming it later is what tells the customer. The
 * receipt therefore says what HAPPENED ("Asked Mucka to pencil in…"), not that
 * a job exists: the job is in Mucka's reply, not in this tap.
 *
 * Every word and time on screen comes off the block. Nothing here formats a
 * date (anti-pattern #41) or computes a slot.
 */
export function SlotPicker({ block, live, onAnswer, onOpen }: {
  block: SlotPickerBlock;
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}) {
  const [picked, setPicked] = useState<string | null>(block.bestId ?? block.slots[0]?.id ?? null);
  const [asked, setAsked] = useState<string | null>(null);
  const radios = useRef<(HTMLButtonElement | null)[]>([]);
  const chosen = block.slots.find(s => s.id === picked) ?? null;
  const locked = !live || asked !== null;

  if (!block.slots.length) {
    return (
      <div className="mchat-slot">
        <p className="mchat-slot-empty">
          No free stretch of {block.lengthLabel} turned up in {block.scope === 'team' ? 'the team diary' : 'your diary'}, {block.searched}, within your business hours.
        </p>
        {block.emptyAsk && (
          <BubbleRows
            label="Look further ahead"
            rows={[{
              id: 'empty-ask',
              title: block.emptyAsk,
              kind: 'answer',
              onPress: () => { if (live) onAnswer(block.emptyAsk!); },
            }]}
          />
        )}
      </div>
    );
  }

  // Arrow keys move the choice, as a radio group should; Tab leaves it.
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const step = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 0;
    if (!step || locked) return;
    e.preventDefault();
    const next = (i + step + block.slots.length) % block.slots.length;
    setPicked(block.slots[next].id);
    radios.current[next]?.focus();
  };

  const pencil = () => {
    if (!chosen || locked) return;
    setAsked(chosen.id);
    onAnswer(chosen.instruction);
  };

  const askedSlot = block.slots.find(s => s.id === asked) ?? null;

  return (
    <div className="mchat-slot">
      <div className="mchat-slot-list" role="radiogroup" aria-label="Pick a time">
        {block.slots.map((s, i) => {
          const on = s.id === picked;
          return (
            <button
              key={s.id}
              ref={el => { radios.current[i] = el; }}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={on ? 0 : -1}
              disabled={locked}
              className={`mchat-slot-row${on ? ' mchat-slot-row--on' : ''}`}
              onClick={() => setPicked(s.id)}
              onKeyDown={e => onKey(e, i)}
            >
              <span className="mchat-slot-when">
                <span className="mchat-slot-day">{s.dayLabel}</span>
                <span className="mchat-slot-time">{s.time}</span>
              </span>
              <span className="mchat-slot-main">
                <span className="mchat-slot-who">
                  {s.who}
                  {s.id === block.bestId && block.bestLabel && <span className="mchat-slot-best">{block.bestLabel}</span>}
                </span>
                <span className="mchat-slot-why">{s.reason}</span>
              </span>
              <span className="mchat-slot-tick" aria-hidden="true">
                {on && <Icon icon={Check} size={12} strokeWidth={3.5} />}
              </span>
            </button>
          );
        })}
      </div>
      <p className="mchat-slot-note">{block.lengthNote}</p>
      {chosen && (
        <BubbleRows
          label="Check the diary"
          rows={[{ id: 'diary', title: `See ${chosen.dayLabel} in the diary`, kind: 'go', onPress: () => onOpen(chosen.diaryPath) }]}
        />
      )}
      <BubbleActions
        actions={[{ label: chosen?.pencilLabel ?? 'Pencil in', onPress: pencil, tone: 'primary', disabled: locked || !chosen }]}
        done={askedSlot ? askedSlot.askedLabel : null}
      />
      <p className="mchat-slot-foot">
        Pencilled only. {block.clientName ?? 'The customer'} isn&apos;t told until you confirm.
      </p>
    </div>
  );
}
